import React from 'react';
import startcase from 'lodash/startCase';
import { customDateFormatter, formatNumericDate, formatDateTimeInET } from './dates';
import { renderTruncatedCell } from './columnTruncation';
import {
  ArrowRight,
  CompletedTrue,
  CompletedFalse,
} from '../componentsV2/Icons/Icons';

import tailwindTheme from '../../../../config/tailwind.config';

// all this functions are used in redesign branch

// Regular expression to match dates in the format M/D/YY
const DATE_REGEX = /^([1-9]|1[012])\/([1-9]|[12][0-9]|3[01])\/\d{2}$/;

// Regular expression to match ISO dates in the format YYYY-MM-DD (from DB custom date fields)
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const SITE_HISTORY_KEYS = {
  note_added: 'newValue',
  note_deleted: 'oldValue',
  tag_added: 'newValue',
  tag_removed: 'oldValue',
  visit_added: 'newValue',
  visit_deleted: 'oldValue',
};

export const formatLabels = (label) => {
  let newLabel = label;
  if (label === 'alternate_ids') {
    newLabel = ('Alternate Id(s)');
  } else if (label === 'h2a') {
    newLabel = 'H2A';
  } else {
    newLabel = startcase(label);
  }
  return newLabel;
};

export const formatValue = (value, label) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    // If the string is empty or contains only whitespace, return 'No Value'
    if (value.trim() === '') return 'No Value';

    // If string value starts and ends with brackets, remove the brackets
    if (value.startsWith('[') && value.endsWith(']')) {
      return value.slice(1, -1);
    }

    // If label is 'arrival' or 'departure', or value matches a date pattern, format the date
    if (['arrival', 'departure'].includes(label) || DATE_REGEX.test(value) || ISO_DATE_REGEX.test(value)) {
      return customDateFormatter(value, 'MM/DD/YYYY');
    }
  }

  // If value is null/undefined, or an object such as raw fieldHistory, return 'No Value'
  if (!value || typeof value === 'object') return 'No Value';

  // If none of the above conditions apply, return the value itself
  return value;
};

export const formatChanges = (siteChanges) => {
  // Return an array of objects that have a label, formattedOldValue, formattedTrueValue
  const formattedChanges = Object.entries(siteChanges).map((change) => {
    // Offline created sites are returning history format a little different
    // The unhandled formatting differences is breaking the details page on offline created sites.
    // As a temp fix adding Nullish coalescing (??) operators  to lines  51 and  ternary operator(?) 54 Both of these should be removed once backend makes changes for a more consistent response
    const fieldLabel = change[0];
    const fieldHistory = change[1];

    const siteHistoryKey = SITE_HISTORY_KEYS[fieldLabel];
    if (siteHistoryKey) {
      const keyValue = fieldHistory?.[siteHistoryKey];
      return {
        label: formatLabels(fieldLabel),
        showArrow: false,
        formattedOldValue: keyValue,
        formattedNewValue: keyValue,
      };
    }

    const { oldValue, newValue } = fieldHistory ?? { oldValue: undefined, newValue: undefined };

    const formattedOldValue = formatValue(oldValue, fieldLabel);
    // newValue comes back as both undefined and null. We only want to use fieldHistory when newValue is undefined.
    // When removing fields (custom date field particularly) our newValue becomes null.
    const formattedNewValue = formatValue(newValue !== undefined ? newValue : fieldHistory, fieldLabel);
    return {
      label: formatLabels(fieldLabel),
      showArrow: true,
      formattedOldValue,
      formattedNewValue,
    };
  });
  return formattedChanges;
};

export const getItemClassNames = (isDragging) => {
  return isDragging ? 'text-vamos-green1 font-bold' : 'text-vamos-gray hover:text-vamos-green1 font-bold';
};
// icon for completed visits
export const visitComplete = (bool) => {
  if (bool) {
    return <CompletedTrue color={tailwindTheme.theme.extend.colors['vamos-green1']} />;
  }
  return <CompletedFalse color={tailwindTheme.theme.extend.colors['vamos-red']} />;
};

export const dataSourceVisits = (data) => (data.map((visit) => ({
  id: visit.id,
  site_id: visit.site_id,
  date: formatNumericDate(visit.date),
  purpose: visit.purpose,
  completed: visitComplete(visit.completed),
  workers_interacted: visit.workers_interacted,
})));

export const dataSourceNotes = (data) => (data.map((note) => {
  let priority;
  if (note.priority === 'high') {
    priority = <div className="text-priority-high">High</div>;
  } else if (note.priority === 'medium') {
    priority = <div className="text-priority-medium">Medium</div>;
  } else if (note.priority === 'low') {
    priority = <div className="text-priority-low">Low</div>;
  } else {
    priority = <div>No priority</div>;
  }
  return {
    id: note.id,
    site_id: note.site_id,
    date: formatNumericDate(note.timestamp),
    content: note.content,
    title: note.title,
    priority,
  };
}));

export const dataSourceContacts = (data) => (data.map((contact) => ({
  id: contact.id,
  name: contact.name,
  phone: contact.phone,
  company: contact.company,
})));

const CONTACT_HISTORY_LABELS = {
  added: 'CONTACT ADDED:',
  removed: 'CONTACT REMOVED:',
  primary_contact_set: 'PRIMARY CONTACT SET:',
  primary_contact_removed: 'PRIMARY CONTACT REMOVED:',
  primary_contact_changed: 'PRIMARY CONTACT CHANGED:',
};

export const dataSourceContactHistory = (data) => {
  return data.map((contactHistory) => {
    const date = formatDateTimeInET(contactHistory.created_at);
    const { id } = contactHistory;
    const userName = contactHistory.user_name;
    const changes = contactHistory.contact_changes || {};
    const label = CONTACT_HISTORY_LABELS[contactHistory.action_type] || 'CONTACT REMOVED:';
    const changeData = contactHistory.action_type === 'primary_contact_changed' ? (
      <div className="flex flex-row gap-1.5 flex-wrap">
        <b> {label}</b> {changes.old_contact_name} <ArrowRight className="my-auto mx-1.5" /> {changes.new_contact_name}
      </div>
    ) : (
      <div className="flex flex-row gap-1.5">
        <b> {label}</b> {changes.contact_name}
      </div>
    );
    return {
      id,
      updated_at: date,
      change: changeData,
      user_name: userName,
    };
  });
};

export const dataSourceSiteHistory = (data) => (
  data.map((siteHistory) => {
    const date = formatDateTimeInET(siteHistory.updated_at);
    const { id } = siteHistory;
    const userName = siteHistory.user_name;
    const formattedChanges = formatChanges(siteHistory.site_changes || {});
    const changeData = formattedChanges.map((change) => (
      change.showArrow ? (
        <div className="flex flex-row gap-1.5 flex-wrap">
          <b> {change.label.toUpperCase()}:</b> {change.formattedOldValue} <ArrowRight className="my-auto mx-1.5" /> {change.formattedNewValue}
        </div>
      ) : (
        <div className="flex flex-row gap-1.5 flex-wrap">
          <b> {change.label.toUpperCase()}:</b> {change.formattedNewValue}
        </div>
      )
    ));
    return {
      id,
      updated_at: date,
      change: changeData,
      user_name: userName,
    };
  }));

export const fetchTabsData = async (APIversion, dataTitle, siteId, setTabsData, orgId, currentPage, limit, setTotalItems, resultCount, showToast) => {
  // handle difference in query params for visits and other tabs (limit vs per_page)
  const params = `per_page=${limit}&page=${currentPage}`;
  await fetch(`/api/${APIversion}/${dataTitle.toLowerCase()}?organization_id=${orgId}&site_id=${siteId}&${params}`)
    .then((response) => response.json())
    .then(newData => {
      setTabsData({
        data: newData[dataTitle.toLowerCase()],
        title: dataTitle,
      });
      setTotalItems(resultCount);
    })
    .catch((error) => {
      showToast({ message: error.message, type: 'error' });
    });
};

export const fetchHistories = async (siteId, dataTitle, historyType, setTabsData, orgId, currentPage, limit, setTotalItems, showToast) => {
  await fetch(`/api/v3/sites/${siteId}/${historyType}?organization_id=${orgId}&&page=${currentPage}&limit=${limit}`)
    .then((response) => response.json())
    .then(newData => {
      setTabsData({
        data: newData.histories,
        title: dataTitle,
      });
      setTotalItems(newData.total_count);
    })
    .catch((error) => {
      showToast({ message: error.message, type: 'error' });
    });
};

export const getVisitsTable = ({ measureRef, maxChars, measuredRowId } = {}) => [
  {
    title: 'Date', dataIndex: 'date', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Visit name',
    dataIndex: 'purpose',
    className: 'basis-auto max-w-80 truncate',
    isSortable: false,
    render: renderTruncatedCell({ measureRef, maxChars, measuredRowId }),
  },
  {
    title: 'Completed', dataIndex: 'completed', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Workers reached', dataIndex: 'workers_interacted', className: 'basis-auto', isSortable: false,
  },
  {
    title: '', dataIndex: '', className: 'lg:w-4/12 flex-1', isSortable: false, // this column push the other columns to the right
  },
];

export const notesTable = [
  {
    title: 'Date', dataIndex: 'date', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Title', dataIndex: 'title', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Content', dataIndex: 'content', className: 'max-w-52 md:max-w-80 lg:max-w-120 truncate', isSortable: false,
  },
  {
    title: 'Priority', dataIndex: 'priority', className: 'basis-auto', isSortable: false,
  },
  {
    title: '', dataIndex: '', className: 'lg:w-6/12 flex-1', isSortable: false, // this column push the other columns to the right
  },
];

export const contactsTable = [
  {
    title: 'Name', dataIndex: 'name', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Phone', dataIndex: 'phone', className: 'basis-auto', isSortable: false,
  },
  {
    title: 'Company', dataIndex: 'company', className: 'basis-auto', isSortable: false,
  },
  {
    title: '', dataIndex: '', className: 'lg:w-4/12 flex-1', isSortable: false, // this column push the other columns to the right
  },

];

export const historyTable = [
  {
    title: 'Date', dataIndex: 'updated_at', className: 'flex-1 content-start', isSortable: false,
  },
  {
    title: 'Changes', dataIndex: 'change', className: 'flex-1', isSortable: false,
  },
  {
    title: 'Author', dataIndex: 'user_name', className: 'flex-1 content-start', isSortable: false,
  },
  {
    title: '', dataIndex: '', className: 'lg:w-4/12 flex-1', isSortable: false, // this column push the other columns to the right
  },
];
